// Bilingual copy for NetoVibe. Keep keywords woven naturally.
export const translations = {
  en: {
    nav: {
      home: "Home",
      about: "About",
      portfolio: "Work",
      pricing: "Pricing",
      contact: "Contact",
      cta: "Get a Quote",
    },
    common: {
      language: "Language",
      currency: "Currency",
      openMenu: "Open menu",
      closeMenu: "Close menu",
      backHome: "Back to home",
    },
    home: {
      metaTitle: "NetoVibe — Affordable Web Design Agency & Custom Development",
      metaDesc:
        "Affordable web design agency crafting custom website development, professional business websites and fast, SEO-ready launches for local & corporate clients.",
      eyebrow: "Web design studio · EN / DE",
      heroTitleA: "Stunning Websites.",
      heroTitleB: "Built to Scale Your Business.",
      heroLead:
        "NetoVibe is an affordable web design agency delivering custom website development for local businesses and corporates. Freelance craft. Agency scale.",
      heroPrimary: "Get a Quote",
      heroSecondary: "See Our Work",
      valuePropTitle: "Professional business websites, engineered like software.",
      valuePropBody:
        "We combine the human touch of a freelance web designer near you with the engineering discipline of an elite agency — so your site loads fast, ranks well, and converts.",
      metrics: [
        { k: "0.9s", v: "Avg. mobile load" },
        { k: "100%", v: "Responsive delivery" },
        { k: "EN·DE", v: "Dual language ready" },
        { k: "7d", v: "Launch pad turnaround" },
      ],
      featuresEyebrow: "What you get",
      featuresTitle: "Everything a professional business website needs.",
      features: [
        {
          t: "Custom website development",
          d: "Hand-coded interfaces tailored to your brand — no bloated page builders, no generic templates.",
        },
        {
          t: "SEO from day one",
          d: "Semantic markup, meta strategy, canonical tags & sitemap generation baked into every build.",
        },
        {
          t: "Bilingual by default",
          d: "English & German content structures with hreflang-ready routing for local + international reach.",
        },
        {
          t: "Mobile-first performance",
          d: "Compressed assets, lazy loading and edge-friendly output for sub-second first paint.",
        },
        {
          t: "Conversion-driven UX",
          d: "Clear hierarchy, obvious CTAs and forms that respect the visitor's time.",
        },
        {
          t: "Post-launch care",
          d: "Ongoing maintenance, security monitoring and content updates — you're never alone after launch.",
        },
      ],
      workEyebrow: "Selected work",
      workTitle: "A visual teaser of what we ship.",
      workLead:
        "Every NetoVibe build starts with clarity of purpose and ends with pixels shipped to production.",
      workCta: "Open case study",
      testimonialsEyebrow: "Client voices",
      testimonialsTitle: "Trusted by local businesses & growing companies.",
      ctaTitle: "Ready to build a website your business deserves?",
      ctaLead:
        "Tell us about your project. We reply within one business day with a transparent quote.",
    },
    about: {
      metaTitle: "About NetoVibe — Freelance Web Designer with Agency Scale",
      metaDesc:
        "A hybrid web design studio: the personal care of a freelance web designer near you, paired with the delivery power of a professional agency team.",
      eyebrow: "About the studio",
      title: "Freelance craft. Agency scale. One dedicated partner.",
      lead:
        "NetoVibe was founded by a passionate developer who believes small businesses deserve the same design quality as Fortune 500 brands — without the Fortune 500 invoice.",
      storyTitle: "The hybrid model",
      story: [
        "Most agencies treat small clients as afterthoughts. Most freelancers can't scale when you grow. NetoVibe was built to fix both problems.",
        "You get one senior developer as your direct partner — the person who codes your site, answers your emails, and cares about your launch. Behind the scenes, a curated network of specialists (design, SEO, copy) plugs in when needed.",
        "The result: professional business websites that feel personal, ship fast, and grow with you.",
      ],
      principlesTitle: "Principles we build by",
      principles: [
        { t: "Own every pixel", d: "No off-the-shelf themes, no visual clichés." },
        { t: "Ship measurably", d: "Every launch reports Core Web Vitals & SEO baseline." },
        { t: "Respect the invoice", d: "Transparent pricing, no hidden surprises." },
        { t: "Speak plainly", d: "In English or Deutsch — never in agency-speak." },
      ],
    },
    portfolio: {
      metaTitle: "Portfolio — Zemka Case Study | NetoVibe Custom Website Development",
      metaDesc:
        "Featured case study: how NetoVibe built a professional business website for Zemka Gebäudereinigung — a trusted local cleaning and janitorial company.",
      eyebrow: "Featured case study",
      title: "Zemka Gebäudereinigung",
      subtitle: "Building a trustworthy digital presence for a top-tier local cleaning & janitorial company.",
      overview: "Project overview",
      overviewBody:
        "Zemka is a family-run cleaning and janitorial service in Penzberg. Until working with NetoVibe, they didn't have a website at all — enquiries came in only by word of mouth and a phone number passed hand to hand. They wanted their online presence to finally match the quality of the work they deliver on the ground.",
      challenges: "The challenge",
      challengesBody:
        "Starting from zero. No existing site, no brand assets sized for the web, no analytics, no lead funnel. We had to build something that felt established from day one — trustworthy enough for commercial clients, simple enough for a busy owner to run without any technical help.",
      solution: "Our solution",
      solutionBody:
        "A fully custom, mobile-first website with a clear service catalogue, five-star trust signals, a booking-ready contact form and a compact SEO strategy targeting 'Gebäudereinigung Penzberg' searches. Delivered under the Professional Business package, with hosting, deployment and a smooth handover included.",
      results: "The results",
      resultsBody:
        "Everything is running smoothly. The site loads in under a second on 4G, Zemka now receives structured enquiries directly to their inbox, and their brand looks the part next to any competitor in the region — often better.",
      devicesEyebrow: "Cross-device delivery",
      devicesTitle: "Designed for every screen in the client's day.",
      moreEyebrow: "More work coming soon",
      moreTitle: "This is just the beginning.",
      moreLead: "We're onboarding new local businesses and small corporates every month. Yours could be next.",
    },
    pricing: {
      metaTitle: "Pricing — Affordable Web Design Packages | NetoVibe",
      metaDesc:
        "Transparent, affordable web design agency pricing. Choose from three packages built for local businesses, growing companies and established enterprises.",
      eyebrow: "Packages",
      title: "Ultra-affordable pricing. Zero compromises on craft.",
      lead:
        "Every package includes semantic SEO, responsive delivery and a live handoff. Pick the tier that fits today — upgrade when you grow.",
      badgeBestValue: "Best Value",
      cta: "Choose plan",
      ctaContact: "Talk to us",
      packages: [
        {
          id: "basic",
          name: "Basic Launch Pad",
          tagline: "For solo operators & single-service local businesses.",
          priceEUR: 600,
          priceUSD: 650,
          features: [
            "1-page responsive landing page",
            "Essential SEO setup",
            "Fast deployment (7 days)",
            "Contact form",
            "1 month of support",
          ],
        },
        {
          id: "pro",
          name: "Professional Business",
          tagline: "For growing companies with a real service catalogue.",
          priceEUR: 1000,
          priceUSD: 1100,
          features: [
            "Up to 5 premium pages",
            "Mobile & tablet optimisation",
            "Advanced contact / booking forms",
            "Basic analytics integration",
            "Speed optimisation",
            "3 months maintenance",
          ],
          highlight: true,
        },
        {
          id: "premium",
          name: "Corporate Premium",
          tagline: "For established enterprises with international ambitions.",
          priceEUR: 1500,
          priceUSD: 1650,
          features: [
            "Fully custom multi-page website",
            "Dual-language setup (EN / DE)",
            "Advanced interactive elements",
            "Complete SEO strategy",
            "Priority VIP support",
            "6 months security monitoring",
          ],
        },
      ],
      compareTitle: "One clear comparison.",
      compareRows: [
        { label: "Pages included", values: ["1", "Up to 5", "Unlimited"] },
        { label: "Dual language (EN/DE)", values: ["—", "Optional", "Included"] },
        { label: "SEO depth", values: ["Essential", "Standard", "Strategic"] },
        { label: "Support window", values: ["1 month", "3 months", "6 months"] },
        { label: "VIP priority", values: ["—", "—", "Included"] },
      ],
    },
    contact: {
      metaTitle: "Contact NetoVibe — Get a Quote for Your Website Project",
      metaDesc:
        "Ready to work with a freelance web designer near you at agency quality? Send us a brief and receive a transparent quote within one business day.",
      eyebrow: "Let's talk",
      title: "Tell us about your project.",
      lead:
        "Share a few details and we'll reply within one business day with a transparent quote and a suggested package.",
      form: {
        name: "Your name",
        email: "Email address",
        company: "Company (optional)",
        businessType: "Business type",
        budget: "Project budget",
        message: "Project details",
        messagePlaceholder:
          "Roughly what do you need? A landing page? A full site rebuild? Any deadlines?",
        submit: "Send the brief",
        submitting: "Sending…",
        success: "Brief received. We'll reply within one business day.",
        error: "Something went wrong. Please try again or email us directly.",
        businessTypeOptions: {
          local_business: "Local business",
          corporate: "Corporate / SMB",
          startup: "Startup",
          ecommerce: "E-commerce",
          personal_brand: "Personal brand",
          other: "Other",
        },
        budgetOptions: {
          under_500: "Under 500",
          "500_1500": "500 – 1,500",
          "1500_5000": "1,500 – 5,000",
          "5000_plus": "5,000+",
          not_sure: "Not sure yet",
        },
        required: "Required",
      },
      details: [
        { k: "Response", v: "Within 1 business day" },
        { k: "Languages", v: "English · Deutsch" },
        { k: "Timezone", v: "Central European Time" },
      ],
    },
    testimonials: [
      {
        quote:
          "Honestly, before NetoVibe we didn't even have a website — everything ran on phone calls and word of mouth. I was a bit nervous about the whole process, but they made it feel simple from the first message. No jargon, no endless meetings, just quick check-ins and real answers. When we went live, my phone actually started buzzing with new enquiries the same week. Now everything just runs smoothly and I finally feel we look as professional online as we are on site. Genuinely could not recommend them more.",
        author: "Owner",
        role: "Zemka Gebäudereinigung & Hausmeister Service",
      },
    ],
    footer: {
      tagline: "Affordable web design agency. Freelance craft. Agency scale.",
      linksTitle: "Sitemap",
      contactTitle: "Contact",
      legalTitle: "Legal",
      privacy: "Privacy",
      imprint: "Imprint",
      rights: "All rights reserved.",
    },
  },

  de: {
    nav: {
      home: "Start",
      about: "Über uns",
      portfolio: "Referenzen",
      pricing: "Preise",
      contact: "Kontakt",
      cta: "Angebot anfordern",
    },
    common: {
      language: "Sprache",
      currency: "Währung",
      openMenu: "Menü öffnen",
      closeMenu: "Menü schließen",
      backHome: "Zurück zur Startseite",
    },
    home: {
      metaTitle: "NetoVibe — Günstige Webdesign Agentur & Professionelle Homepage Erstellung",
      metaDesc:
        "Günstige Webdesign Agentur für professionelle Homepage Erstellung. Wir sind Webdesigner für Firmen und lassen Ihre Website schnell und SEO-stark erstellen.",
      eyebrow: "Webdesign Studio · EN / DE",
      heroTitleA: "Herausragende Websites.",
      heroTitleB: "Gebaut, um Ihr Business zu skalieren.",
      heroLead:
        "NetoVibe ist Ihre günstige Webdesign Agentur für professionelle Homepage Erstellung. Persönlich wie ein Freelancer. Skalierbar wie eine Agentur.",
      heroPrimary: "Angebot anfordern",
      heroSecondary: "Referenzen ansehen",
      valuePropTitle: "Professionelle Firmen-Websites, entwickelt wie Software.",
      valuePropBody:
        "Wir verbinden die persönliche Betreuung eines Webdesigners für Firmen mit der Disziplin einer erstklassigen Agentur — Ihre Website lädt schnell, rankt sauber und konvertiert.",
      metrics: [
        { k: "0,9s", v: "Mobile Ladezeit" },
        { k: "100%", v: "Responsive Lieferung" },
        { k: "EN·DE", v: "Zweisprachig fertig" },
        { k: "7d", v: "Launch Pad Turnaround" },
      ],
      featuresEyebrow: "Was Sie erhalten",
      featuresTitle: "Alles, was eine professionelle Firmen-Website braucht.",
      features: [
        {
          t: "Individuelle Website-Entwicklung",
          d: "Handgeschriebene Interfaces, exakt auf Ihre Marke zugeschnitten — keine überladenen Baukästen.",
        },
        {
          t: "SEO ab Tag eins",
          d: "Semantisches Markup, Meta-Strategie, Canonical Tags und Sitemap in jedem Build inklusive.",
        },
        {
          t: "Zweisprachig von Haus aus",
          d: "Strukturen für Deutsch und Englisch mit hreflang-fähigem Routing für lokale und internationale Reichweite.",
        },
        {
          t: "Mobile-first Performance",
          d: "Komprimierte Assets, Lazy Loading und Edge-freundlicher Output für einen First Paint unter einer Sekunde.",
        },
        {
          t: "Konversionsstarke UX",
          d: "Klare Hierarchie, offensichtliche CTAs und Formulare, die die Zeit Ihrer Besucher respektieren.",
        },
        {
          t: "Betreuung nach dem Launch",
          d: "Wartung, Security Monitoring und Content-Updates — nach dem Go-Live sind Sie nie allein.",
        },
      ],
      workEyebrow: "Ausgewählte Arbeiten",
      workTitle: "Ein visueller Vorgeschmack auf unsere Arbeit.",
      workLead:
        "Jedes NetoVibe-Projekt startet mit klarer Zielsetzung und endet mit Pixeln in Produktion.",
      workCta: "Case Study öffnen",
      testimonialsEyebrow: "Kundenstimmen",
      testimonialsTitle: "Vertraut von lokalen Betrieben und wachsenden Firmen.",
      ctaTitle: "Bereit für eine Website, die Ihr Unternehmen verdient?",
      ctaLead:
        "Erzählen Sie uns von Ihrem Projekt. Innerhalb eines Werktags erhalten Sie ein transparentes Angebot.",
    },
    about: {
      metaTitle: "Über NetoVibe — Webdesigner für Firmen mit Agentur-Power",
      metaDesc:
        "Ein hybrides Webdesign-Studio: persönliche Betreuung wie beim Freelancer, Umsetzungsstärke wie in einer Agentur. Website erstellen lassen — persönlich und professionell.",
      eyebrow: "Über das Studio",
      title: "Handwerk vom Freelancer. Skalierung wie in der Agentur.",
      lead:
        "NetoVibe wurde von einem leidenschaftlichen Entwickler gegründet, der überzeugt ist: Kleine und mittlere Firmen verdienen dieselbe Designqualität wie Großkonzerne — ohne deren Rechnung.",
      storyTitle: "Das hybride Modell",
      story: [
        "Die meisten Agenturen behandeln kleine Kunden nebenbei. Freelancer wiederum stoßen an Grenzen, sobald Sie wachsen. NetoVibe löst beide Probleme.",
        "Sie bekommen einen Senior-Entwickler als direkten Ansprechpartner — die Person, die Ihre Website programmiert, Ihre E-Mails beantwortet und sich um Ihren Launch kümmert. Im Hintergrund docken kuratierte Spezialisten (Design, SEO, Copy) an, wo nötig.",
        "Das Ergebnis: professionelle Firmen-Websites, die persönlich wirken, schnell live gehen und mit Ihnen wachsen.",
      ],
      principlesTitle: "Prinzipien, nach denen wir bauen",
      principles: [
        { t: "Jeder Pixel gehört Ihnen", d: "Keine Standard-Themes, keine visuellen Klischees." },
        { t: "Messbar liefern", d: "Jeder Launch wird mit Core Web Vitals und SEO-Baseline dokumentiert." },
        { t: "Die Rechnung respektieren", d: "Transparente Preise, keine versteckten Überraschungen." },
        { t: "Klar sprechen", d: "Auf Deutsch oder Englisch — nie im Agentur-Slang." },
      ],
    },
    portfolio: {
      metaTitle: "Referenzen — Case Study Zemka | NetoVibe Website erstellen lassen",
      metaDesc:
        "Referenz-Projekt: Wie NetoVibe für Zemka Gebäudereinigung eine professionelle Firmen-Website erstellt hat — vertrauenswürdig, schnell und lokal stark.",
      eyebrow: "Featured Case Study",
      title: "Zemka Gebäudereinigung",
      subtitle: "Wie wir für einen führenden lokalen Reinigungs- und Hausmeisterservice eine vertrauenswürdige digitale Präsenz aufgebaut haben.",
      overview: "Projektübersicht",
      overviewBody:
        "Zemka ist ein familiengeführter Reinigungs- und Hausmeisterservice aus Penzberg. Bis zur Zusammenarbeit mit NetoVibe hatten sie überhaupt keine Website — Anfragen kamen nur über Mundpropaganda und eine Telefonnummer, die von Hand zu Hand ging. Der Online-Auftritt sollte endlich der Qualität entsprechen, die sie vor Ort tatsächlich abliefern.",
      challenges: "Die Herausforderung",
      challengesBody:
        "Bei Null anfangen. Keine bestehende Website, keine web-tauglichen Markenassets, keine Analytics, kein Anfrage-Funnel. Wir mussten etwas bauen, das vom ersten Tag an etabliert wirkt — vertrauenswürdig genug für gewerbliche Kunden, gleichzeitig so einfach zu bedienen, dass der Inhaber es ohne technische Hilfe pflegen kann.",
      solution: "Unsere Lösung",
      solutionBody:
        "Eine vollständig individuelle, mobile-first Website mit klarem Leistungskatalog, 5-Sterne-Vertrauenssignalen, Buchungs-Kontaktformular und einer kompakten SEO-Strategie auf 'Gebäudereinigung Penzberg'. Umgesetzt im Paket Professional Business — inklusive Hosting, Deployment und einem sauberen Handover.",
      results: "Das Ergebnis",
      resultsBody:
        "Alles läuft rund. Die Seite lädt in unter einer Sekunde auf 4G, Zemka bekommt strukturierte Anfragen direkt ins Postfach, und ihre Marke sieht neben jedem Mitbewerber in der Region absolut wettbewerbsfähig aus — oft besser.",
      devicesEyebrow: "Auslieferung auf jedem Gerät",
      devicesTitle: "Designed für jeden Screen im Kundenalltag.",
      moreEyebrow: "Weitere Projekte in Kürze",
      moreTitle: "Das ist erst der Anfang.",
      moreLead: "Wir nehmen jeden Monat neue lokale Firmen und kleine Unternehmen auf. Ihres könnte das nächste sein.",
    },
    pricing: {
      metaTitle: "Preise — Günstige Webdesign Pakete | NetoVibe",
      metaDesc:
        "Transparente Preise einer günstigen Webdesign Agentur. Drei Pakete für lokale Firmen, wachsende Unternehmen und etablierte Marken.",
      eyebrow: "Pakete",
      title: "Ultra-günstige Preise. Null Kompromisse beim Handwerk.",
      lead:
        "Jedes Paket enthält semantisches SEO, responsive Auslieferung und einen sauberen Handover. Wählen Sie heute — upgraden Sie, wenn Sie wachsen.",
      badgeBestValue: "Beliebteste Wahl",
      cta: "Paket wählen",
      ctaContact: "Uns schreiben",
      packages: [
        {
          id: "basic",
          name: "Basic Launch Pad",
          tagline: "Für Einzelunternehmer und lokale Betriebe mit einer Dienstleistung.",
          priceEUR: 600,
          priceUSD: 650,
          features: [
            "1-seitige responsive Landingpage",
            "Grundlegende SEO-Einrichtung",
            "Schneller Launch (7 Tage)",
            "Kontaktformular",
            "1 Monat Support",
          ],
        },
        {
          id: "pro",
          name: "Professional Business",
          tagline: "Für wachsende Firmen mit echtem Leistungskatalog.",
          priceEUR: 1000,
          priceUSD: 1100,
          features: [
            "Bis zu 5 Premium-Seiten",
            "Mobile & Tablet Optimierung",
            "Erweiterte Kontakt-/Buchungsformulare",
            "Analytics-Grundintegration",
            "Speed-Optimierung",
            "3 Monate Wartung",
          ],
          highlight: true,
        },
        {
          id: "premium",
          name: "Corporate Premium",
          tagline: "Für etablierte Unternehmen mit internationalem Anspruch.",
          priceEUR: 1500,
          priceUSD: 1650,
          features: [
            "Vollständig individuelle Multi-Page-Website",
            "Zweisprachiges Setup (EN / DE)",
            "Fortgeschrittene interaktive Elemente",
            "Komplette SEO-Strategie",
            "VIP Priority Support",
            "6 Monate Security Monitoring",
          ],
        },
      ],
      compareTitle: "Ein klarer Vergleich.",
      compareRows: [
        { label: "Enthaltene Seiten", values: ["1", "Bis zu 5", "Unbegrenzt"] },
        { label: "Zweisprachig (EN/DE)", values: ["—", "Optional", "Enthalten"] },
        { label: "SEO-Tiefe", values: ["Grundlagen", "Standard", "Strategisch"] },
        { label: "Support-Zeitraum", values: ["1 Monat", "3 Monate", "6 Monate"] },
        { label: "VIP Priority", values: ["—", "—", "Enthalten"] },
      ],
    },
    contact: {
      metaTitle: "Kontakt — NetoVibe | Website erstellen lassen",
      metaDesc:
        "Bereit, mit einem Webdesigner für Firmen zusammenzuarbeiten? Schicken Sie uns ein kurzes Briefing — innerhalb eines Werktags erhalten Sie ein transparentes Angebot.",
      eyebrow: "Reden wir",
      title: "Erzählen Sie uns von Ihrem Projekt.",
      lead:
        "Ein paar Details reichen — innerhalb eines Werktags erhalten Sie ein transparentes Angebot inklusive Paketempfehlung.",
      form: {
        name: "Ihr Name",
        email: "E-Mail-Adresse",
        company: "Firma (optional)",
        businessType: "Art des Unternehmens",
        budget: "Projektbudget",
        message: "Projektdetails",
        messagePlaceholder:
          "Was benötigen Sie ungefähr? Eine Landingpage? Einen kompletten Relaunch? Deadlines?",
        submit: "Briefing senden",
        submitting: "Senden…",
        success: "Briefing erhalten. Antwort innerhalb eines Werktags.",
        error: "Etwas ist schiefgelaufen. Bitte erneut versuchen oder uns direkt schreiben.",
        businessTypeOptions: {
          local_business: "Lokaler Betrieb",
          corporate: "Unternehmen / SMB",
          startup: "Startup",
          ecommerce: "E-Commerce",
          personal_brand: "Personal Brand",
          other: "Andere",
        },
        budgetOptions: {
          under_500: "Unter 500",
          "500_1500": "500 – 1.500",
          "1500_5000": "1.500 – 5.000",
          "5000_plus": "5.000+",
          not_sure: "Noch unklar",
        },
        required: "Pflichtfeld",
      },
      details: [
        { k: "Antwort", v: "Innerhalb eines Werktags" },
        { k: "Sprachen", v: "Deutsch · English" },
        { k: "Zeitzone", v: "Mitteleuropäische Zeit" },
      ],
    },
    testimonials: [
      {
        quote:
          "Ehrlich gesagt hatten wir vor NetoVibe überhaupt keine Website — alles lief über Anrufe und Mundpropaganda. Ich war anfangs etwas skeptisch, aber sie haben das Ganze von der ersten Nachricht an angenehm einfach gemacht. Kein Fachchinesisch, keine ewigen Meetings, sondern kurze Absprachen und echte Antworten. Als wir online gingen, klingelte in derselben Woche tatsächlich das Handy mit neuen Anfragen. Heute läuft einfach alles rund und ich habe endlich das Gefühl, online genauso professionell zu wirken, wie wir es vor Ort auch sind. Klare Empfehlung, wirklich.",
        author: "Inhaber",
        role: "Zemka Gebäudereinigung & Hausmeisterservice",
      },
    ],
    footer: {
      tagline: "Günstige Webdesign Agentur. Freelancer-Handwerk. Agentur-Skalierung.",
      linksTitle: "Sitemap",
      contactTitle: "Kontakt",
      legalTitle: "Rechtliches",
      privacy: "Datenschutz",
      imprint: "Impressum",
      rights: "Alle Rechte vorbehalten.",
    },
  },
};

export const SUPPORTED_LANGS = ["en", "de"];
export const SUPPORTED_CURRENCIES = ["EUR", "USD"];
